import logger from '@/utils/client-logger';
import { env } from '@/utils/env';
import { Provider, ProviderType, TrackingEvent } from '../types';

interface RtbHouseConfig {
  trackingId: string;
  dataCenter?: string; // e.g., 'us', 'eu', etc.
  isEnabled?: boolean;
}

declare global {
  interface Window {
    rtbhEvents?: any[];
  }
}

export class RtbHouseProvider implements Provider {
  type = ProviderType.RTB_HOUSE;
  isEnabled: boolean;
  loggingEnabled = false; // 公开属性，支持ReportingCenter访问
  private initialized = false;
  private config: RtbHouseConfig;

  constructor(config: RtbHouseConfig) {
    this.config = config;
    this.isEnabled = config.isEnabled ?? true;
    this.loggingEnabled = env.NEXT_PUBLIC_ENV === 'local';
  }

  async init(): Promise<boolean> {
    try {
      if (typeof window === 'undefined') return false;
      if (this.initialized) return true;

      const { trackingId } = this.config;
      const dataCenter = this.config.dataCenter || 'us';

      // RTB House initialization script based on provided code
      return new Promise((resolve) => {
        const w = window;
        const d = document;
        const dn = 'rtbhEvents';
        const t = trackingId;

        // Initialize rtbhEvents array if not exists and push init event
        w[dn] = w[dn] || [];
        w[dn].push({
          eventType: 'init',
          value: t,
          dc: dataCenter,
        });

        // Create and inject the tracking script
        const f = d.getElementsByTagName('script')[0];
        const c = d.createElement('script') as HTMLScriptElement;
        c.async = true;
        c.src = `https://tags.creativecdn.com/${trackingId}.js`;

        c.onload = () => {
          this.initialized = true;
          if (this.loggingEnabled) {
            logger.info('RTB House tracking initialized successfully');
          }
          resolve(true);
        };

        c.onerror = (error) => {
          logger.error('Failed to load RTB House tracking script:', error);
          resolve(false);
        };

        if (f && f.parentNode) {
          f.parentNode.insertBefore(c, f);
        } else {
          // Fallback: append to head if no script tags exist
          document.head.appendChild(c);
        }
      });
    } catch (error) {
      logger.error('Failed to initialize RTB House tracking:', error);
      return false;
    }
  }

  track(event: TrackingEvent): void {
    if (!this.initialized || typeof window === 'undefined') return;

    try {
      if (window.rtbhEvents && Array.isArray(window.rtbhEvents)) {
        if (this.loggingEnabled) {
          logger.info('RTB House tracking event:', event);
        }

        // Push event to RTB House events queue
        window.rtbhEvents.push(
          {
            eventType: event.eventName,
            ...event.properties,
          },
          {
            eventType: 'uid',
            id: localStorage.getItem('zl-userId') || 'unknown',
          },
        );
      }
    } catch (error) {
      logger.error('RTB House tracking error:', error);
    }
  }

  /**
   * 启用RTB House日志
   */
  enableLog(): void {
    this.loggingEnabled = true;
  }

  /**
   * 禁用RTB House日志
   */
  disableLog(): void {
    this.loggingEnabled = false;
  }

  isInitialized(): boolean {
    return this.initialized;
  }
}
